import React, { useEffect, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    CircularProgress,
    Grid,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { makeGETRequestOnUserService } from "../../Api/api";

interface PreVerificationDetailsProps {
    userUUID: string;
}

const Field = ({
    label,
    value,
}: {
    label: string;
    value: any;
}) => (
    <Grid item xs={12} sm={6} md={3}>
        <Box>
            <Typography
                sx={{
                    fontSize: "12px",
                    color: "#777",
                    mb: 0.5,
                }}
            >
                {label}
            </Typography>

            <Typography
                sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#262349",
                    wordBreak: "break-word",
                }}
            >
                {value !== null &&
                    value !== undefined &&
                    value !== ""
                    ? String(value)
                    : "-"}
            </Typography>
        </Box>
    </Grid>
);

const Section = ({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}) => (
    <Accordion
        defaultExpanded
        sx={{
            mb: 2,
            borderRadius: "8px !important",
            border: "1px solid #e4e5eb",
            boxShadow: "none",
            "&:before": {
                display: "none",
            },
        }}
    >
        <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
                backgroundColor: "#f7f7f9",
                minHeight: 60,
                borderRadius: "8px",
                "&.Mui-expanded": {
                    minHeight: 60,
                    borderBottom: "1px solid #e4e5eb",
                },
                "& .MuiAccordionSummary-content": {
                    margin: "12px 0",
                },
            }}
        >
            <Typography
                sx={{
                    fontSize: "17px",
                    fontWeight: 700,
                    color: "#262349",
                }}
            >
                {title}
            </Typography>
        </AccordionSummary>

        <AccordionDetails
            sx={{
                p: 2.5,
                backgroundColor: "#fff",
            }}
        >
            {children}
        </AccordionDetails>
    </Accordion>
);

const formatValue = (value: any) => {
    if (value === null || value === undefined || value === "") {
        return "-";
    }

    if (typeof value === "boolean") {
        return value ? "Yes" : "No";
    }

    return value;
};

const formatDate = (date: string | null | undefined) => {
    if (!date) {
        return "-";
    }

    try {
        return new Date(date).toLocaleString("en-IN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
    } catch {
        return date;
    }
};

const PreVerificationDetails = ({
    userUUID,
}: PreVerificationDetailsProps) => {
    const [preVerificationData, setPreVerificationData] =
        useState<any>(null);

    const [loading, setLoading] = useState(true);

    const toastr = require("toastr");

    const fetchPreVerification = async () => {
        if (!userUUID) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);

            const response = await makeGETRequestOnUserService(
                `/mutual_fund/pre_verification/get/${userUUID}/`
            );

            if (response?.status === 200) {
                setPreVerificationData(
                    response?.data?.data || response?.data
                );
            } else {
                toastr.error(
                    response?.data?.error ||
                    response?.data?.message ||
                    "Failed to load pre-verification details"
                );
            }
        } catch (error: any) {
            console.error(
                "Pre-Verification Details Error:",
                error
            );

            toastr.error(
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                "Failed to load pre-verification details"
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPreVerification();
    }, [userUUID]);

    if (loading) {
        return (
            <Box
                sx={{
                    height: 400,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 2,
                }}
            >
                <CircularProgress
                    sx={{ color: "#262349" }}
                />

                <Typography color="text.secondary">
                    Loading Pre-Verification Details...
                </Typography>
            </Box>
        );
    }

    if (!preVerificationData) {
        return (
            <Box
                sx={{
                    p: 4,
                    textAlign: "center",
                }}
            >
                <Typography color="text.secondary">
                    No pre-verification details available.
                </Typography>
            </Box>
        );
    }

    const bankAccounts =
        preVerificationData?.bank_accounts || [];

    return (
        <Box
            sx={{
                width: "100%",
                backgroundColor: "#f5f6fa",
                p: { xs: 1, sm: 2 },
            }}
        >
            <Section title="Basic Details">
                <Grid container spacing={3}>
                    <Field
                        label="Pre-Verification ID"
                        value={preVerificationData?.id}
                    />

                    <Field
                        label="Object"
                        value={preVerificationData?.object}
                    />

                    <Field
                        label="Status"
                        value={preVerificationData?.status}
                    />

                    <Field
                        label="Investor Identifier"
                        value={
                            preVerificationData?.investor_identifier
                        }
                    />
                </Grid>
            </Section>

            <Section title="PAN Details">
                <Grid container spacing={3}>
                    <Field
                        label="PAN"
                        value={preVerificationData?.pan?.value}
                    />

                    <Field
                        label="PAN Status"
                        value={preVerificationData?.pan?.status}
                    />

                    <Field
                        label="PAN Code"
                        value={formatValue(
                            preVerificationData?.pan?.code
                        )}
                    />

                    <Field
                        label="PAN Reason"
                        value={formatValue(
                            preVerificationData?.pan?.reason
                        )}
                    />
                </Grid>
            </Section>

            <Section title="Name Details">
                <Grid container spacing={3}>
                    <Field
                        label="Name"
                        value={preVerificationData?.name?.value}
                    />

                    <Field
                        label="Name Status"
                        value={preVerificationData?.name?.status}
                    />

                    <Field
                        label="Name Code"
                        value={formatValue(
                            preVerificationData?.name?.code
                        )}
                    />

                    <Field
                        label="Name Reason"
                        value={formatValue(
                            preVerificationData?.name?.reason
                        )}
                    />
                </Grid>
            </Section>

            <Section title="Date of Birth">
                <Grid container spacing={3}>
                    <Field
                        label="Date of Birth"
                        value={
                            preVerificationData?.date_of_birth?.value
                        }
                    />

                    <Field
                        label="Status"
                        value={
                            preVerificationData?.date_of_birth?.status
                        }
                    />

                    <Field
                        label="Code"
                        value={formatValue(
                            preVerificationData?.date_of_birth?.code
                        )}
                    />

                    <Field
                        label="Reason"
                        value={formatValue(
                            preVerificationData?.date_of_birth?.reason
                        )}
                    />
                </Grid>
            </Section>

            <Section title="Bank Account Details">
                {bankAccounts.length > 0 ? (
                    bankAccounts.map(
                        (account: any, index: number) => (
                            <Box
                                key={index}
                                sx={{
                                    mb:
                                        index <
                                            bankAccounts.length - 1
                                            ? 3
                                            : 0,
                                    p: 2,
                                    border: "1px solid #e4e5eb",
                                    borderRadius: "8px",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: "15px",
                                        fontWeight: 700,
                                        color: "#262349",
                                        mb: 2,
                                    }}
                                >
                                    Bank Account {index + 1}
                                </Typography>

                                <Grid container spacing={3}>
                                    <Field
                                        label="Account Number"
                                        value={
                                            account?.value
                                                ?.account_number
                                        }
                                    />

                                    <Field
                                        label="IFSC Code"
                                        value={
                                            account?.value?.ifsc_code
                                        }
                                    />

                                    <Field
                                        label="Account Type"
                                        value={
                                            account?.value?.account_type
                                        }
                                    />

                                    <Field
                                        label="Status"
                                        value={account?.status}
                                    />

                                    <Field
                                        label="Code"
                                        value={formatValue(
                                            account?.code
                                        )}
                                    />

                                    <Field
                                        label="Reason"
                                        value={formatValue(
                                            account?.reason
                                        )}
                                    />

                                    <Field
                                        label="Bank Account Proof"
                                        value={formatValue(
                                            account?.value
                                                ?.bank_account_proof
                                        )}
                                    />
                                </Grid>
                            </Box>
                        )
                    )
                ) : (
                    <Typography color="text.secondary">
                        No bank account details available.
                    </Typography>
                )}
            </Section>

            <Section title="Readiness">
                <Grid container spacing={3}>
                    <Field
                        label="Status"
                        value={
                            preVerificationData?.readiness?.status
                        }
                    />

                    <Field
                        label="Code"
                        value={formatValue(
                            preVerificationData?.readiness?.code
                        )}
                    />

                    <Field
                        label="Reason"
                        value={formatValue(
                            preVerificationData?.readiness?.reason
                        )}
                    />

                    <Field
                        label="Modification"
                        value={formatValue(
                            preVerificationData?.readiness
                                ?.modification
                        )}
                    />
                </Grid>
            </Section>

            <Section title="Pre-Verification Timeline">
                <Grid container spacing={3}>
                    <Field
                        label="Created At"
                        value={formatDate(
                            preVerificationData?.created_at
                        )}
                    />

                    <Field
                        label="Updated At"
                        value={formatDate(
                            preVerificationData?.updated_at
                        )}
                    />

                    <Field
                        label="Completed At"
                        value={formatDate(
                            preVerificationData?.completed_at
                        )}
                    />
                </Grid>
            </Section>
        </Box>
    );
};

export default PreVerificationDetails;
